import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { CheckCircle, XCircle, ArrowRight, ArrowLeft, Copy, Check } from 'lucide-react';
import { generateQuizForSession, fetchLatestQuiz, isQuizRequestInFlight, generateQuizFromContent } from '../../services/quizService';
import useLimits from '../../hooks/useLimits';

export default function QuizSection({ contentId, content }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  
  const [quizData, setQuizData] = useState([]);
  const [isGenerating, setIsGenerating] = useState(true);
  const [generationError, setGenerationError] = useState(null);
  // Store selected option as its index now (number) instead of the option text
  const [selectedOption, setSelectedOption] = useState(null); // holds index or null
  const [answerStatus, setAnswerStatus] = useState('unanswered'); // 'unanswered', 'correct', 'incorrect', 'skipped'
  const [timer, setTimer] = useState(0);
  const [questionAnimation, setQuestionAnimation] = useState('animate-fade-in');
  const [isCopied, setIsCopied] = useState(false);
  const { quiz: quizLimit } = useLimits();

  // NEW: helper to resolve the raw "correct answer" value from various fields
  const getRawCorrectAnswer = (q) => {
    if (!q) return null;
    if (q.correctAnswer !== undefined && q.correctAnswer !== null) return q.correctAnswer;
    if (q.answer !== undefined && q.answer !== null) return q.answer;
    if (q.correct_answer !== undefined && q.correct_answer !== null) return q.correct_answer;
    if (q.correct_option !== undefined && q.correct_option !== null) return q.correct_option;
    return null;
  };

  // NEW: robust resolver that converts correctAnswer (string or index) into a numeric index
  const resolveCorrectIndex = (q) => {
    if (!q || !Array.isArray(q.options)) return 0;
    const opts = q.options;
    const raw = getRawCorrectAnswer(q);

    // If a numeric index already
    if (typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw < opts.length) return raw;

    // If a numeric-like string "0", "1", ...
    if (typeof raw === 'string' && /^\d+$/.test(raw)) {
      const idx = Number(raw);
      if (idx >= 0 && idx < opts.length) return idx;
    }

    // If a one-letter code like "A"/"B"/"C"/"D"
    if (typeof raw === 'string' && /^[A-D]$/i.test(raw.trim())) {
      const code = raw.trim().toUpperCase().charCodeAt(0) - 65;
      if (code >= 0 && code < opts.length) return code;
    }

    // If formats like "Answer: C", "Option B", "B.", etc.
    if (typeof raw === 'string') {
      const m = raw.trim().match(/(?:^|[\s:])(A|B|C|D)(?:[.)\s]|$)/i);
      if (m) {
        const code = m[1].toUpperCase().charCodeAt(0) - 65;
        if (code >= 0 && code < opts.length) return code;
      }
    }

    // Prefer exact text match (trimmed)
    if (typeof raw === 'string') {
      const exactIdx = opts.findIndex(opt => (opt || '').trim() === raw.trim());
      if (exactIdx !== -1) return exactIdx;

      // Case-insensitive exact match
      const lower = raw.trim().toLowerCase();
      const ciIdx = opts.findIndex(opt => (opt || '').trim().toLowerCase() === lower);
      if (ciIdx !== -1) return ciIdx;

      // Fallback: contains (case-insensitive)
      const containsIdx = opts.findIndex(opt => (opt || '').toLowerCase().includes(lower));
      if (containsIdx !== -1) return containsIdx;
    }

    // Final fallback
    return 0;
  };

  // Helper: normalize correctAnswer to numeric index (without fragile A-D scanning inside long strings)
  const normalizeQuizQuestions = (questions = []) => {
    return questions.map(q => {
      const idx = resolveCorrectIndex(q);
      return { ...q, correctAnswer: idx }; // store as index for consistent rendering
    });
  };

   // Check localStorage first, then generate if needed
   useEffect(() => {
    let cancelled = false;
    (async () => {
       if (contentId) {
         try {
           const latest = await fetchLatestQuiz(contentId);
           if (latest && Array.isArray(latest.cards) && latest.cards.length > 0) {
             if (!cancelled) {
               const normalized = normalizeQuizQuestions(latest.cards);
               setQuizData(normalized);
               setIsGenerating(false);
             }
             return;
           }
         } catch (e) {}
       }
 
       // Avoid duplicate generate calls when one is already in-flight
       if (contentId && isQuizRequestInFlight(contentId)) return;

       // No saved quiz found, generate a new one
       await generateQuiz();
     })();
 
     return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentId]);
 
   useEffect(() => {
     let interval;
     if (!showResults && quizData.length > 0) {
       interval = setInterval(() => {
         setTimer(prev => prev + 1);
       }, 1000);
     }
     return () => clearInterval(interval);
   }, [showResults, quizData]);
 
  const generateQuiz = async (forceRegenerate = false) => {
    // ✅ block if limit used up (unless force regenerate)
    if (quizLimit !== undefined && quizLimit <= 0 && !forceRegenerate) {
      setGenerationError("You have reached your daily quiz limit.");
      setIsGenerating(false);
      return;
    }

    // Skip if a request is already running for this content
    if (contentId && isQuizRequestInFlight(contentId)) return;

    setIsGenerating(true);
    setGenerationError(null);
     try {
       const contextContent = content || "";
      let quizQuestions = null;

      if (contentId) {
        quizQuestions = await generateQuizForSession(contentId);
      } else {
        quizQuestions = await generateQuizFromContent(contextContent);
      }

      if (!Array.isArray(quizQuestions) || quizQuestions.length === 0) {
        throw new Error("Generated quiz is empty or invalid");
      }
      const normalized = normalizeQuizQuestions(quizQuestions);
      setQuizData(normalized);
      setCurrentQuestion(0);
      setSelectedAnswers({});
      setShowResults(false);
      setSelectedOption(null);
      setAnswerStatus('unanswered');
      setTimer(0);
 
      // Removed decrementLimit call as backend handles it
     } catch (error) {
       setGenerationError("Failed to generate quiz questions. Please try again.")
     } finally {
       setIsGenerating(false);
     }
   };
 
   const formatTime = (seconds) => {
     const mins = Math.floor(seconds / 60);
     const secs = seconds % 60;
     return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
   };

   const handleOptionSelect = (optionIndex) => {
     if (answerStatus === 'unanswered') {
       setSelectedOption(optionIndex);
     }
   };

   const handleCheckAnswer = () => {
     if (selectedOption === null) return;
     const current = quizData[currentQuestion];
     const correctIndex = resolveCorrectIndex(current); // CHANGED: derive robustly
     const isCorrect = Number(selectedOption) === Number(correctIndex);
     setAnswerStatus(isCorrect ? 'correct' : 'incorrect');
     setSelectedAnswers(prev => ({ ...prev, [current.id]: selectedOption }));
   };

   const handleDontKnow = () => {
     setAnswerStatus('skipped');
     setSelectedAnswers(prev => ({ ...prev, [quizData[currentQuestion].id]: 'skipped' }));
   };

   const handleNextQuestion = () => {
     setQuestionAnimation('animate-slide-out');
     setTimeout(() => {
      if (currentQuestion < quizData.length - 1) {
        setCurrentQuestion(prev => prev + 1);
        setSelectedOption(null);
        setAnswerStatus('unanswered');
        setQuestionAnimation('animate-slide-in');
      } else {
        setShowResults(true);
      }
     }, 300);
   };
  
   const handleTryAgain = () => {
     setCurrentQuestion(0);
     setSelectedAnswers({});
     setShowResults(false);
     setSelectedOption(null);
     setAnswerStatus('unanswered');
     setTimer(0);
   };

   const calculateScore = () => {
     let correct = 0;
     quizData.forEach(q => {
       const ans = selectedAnswers[q.id];
       if (ans !== 'skipped' && ans !== undefined && Number(ans) === Number(q.correctAnswer)) correct++;
     });
     return correct;
   };

   const handleCopy = () => {
     if (currentQ) {
       navigator.clipboard.writeText(currentQ.question);
       setIsCopied(true);
       setTimeout(() => setIsCopied(false), 1500);
     }
   };
 
   const currentQ = quizData.length > 0 ? quizData[currentQuestion] : null;
 
   // Navigation controls with improved UI
   const handlePrevQuestion = () => {
     if (currentQuestion > 0) {
       setQuestionAnimation('animate-slide-out');
       setTimeout(() => {
         const targetIndex = currentQuestion - 1;
         setCurrentQuestion(targetIndex);
         const prevQ = quizData[targetIndex];
         const prevAnswer = selectedAnswers[prevQ.id];
         if (prevAnswer === 'skipped') {
           setSelectedOption(null);
           setAnswerStatus('skipped');
         } else if (prevAnswer !== undefined) {
           setSelectedOption(prevAnswer);
           const isCorrect = Number(prevAnswer) === Number(prevQ.correctAnswer);
           setAnswerStatus(isCorrect ? 'correct' : 'incorrect');
         } else {
           setSelectedOption(null);
           setAnswerStatus('unanswered');
         }
         setQuestionAnimation('animate-slide-in');
       }, 300);
     }
   };
 
   if (isGenerating) {
     return (
       <div className="h-full overflow-y-auto p-4 md:p-6 bg-white flex items-center justify-center">
         <div className="text-center">
           <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 mb-4"></div>
           <h3 className="text-lg font-medium text-gray-900">Generating Quiz Questions</h3>
           <p className="text-sm text-gray-500 mt-2">This may take a few moments...</p>
         </div>
       </div>
     );
   }

   if (generationError) {
     return (
       <div className="h-full overflow-y-auto p-4 md:p-6 bg-white flex flex-col items-center justify-center">
         <div className="text-center max-w-md">
           <XCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
           <h3 className="text-lg font-medium text-gray-900">Failed to Generate Quiz</h3>
           <p className="text-sm text-gray-500 mt-2 mb-6">{generationError}</p>
           <button 
             onClick={generateQuiz} 
             className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
           >
             Try Again
           </button>
         </div>
       </div>
     );
   }

   if (!currentQ) {
     return (
       <div className="h-full overflow-y-auto p-4 md:p-6 bg-white flex items-center justify-center">
         <div className="text-center">
           <h3 className="text-lg font-medium text-gray-900">No quiz questions available</h3>
           <button 
             onClick={generateQuiz} 
             className="px-4 py-2 mt-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
           >
             Generate Quiz
           </button>
         </div>
       </div>
     );
   }

   if (showResults) {
     const score = calculateScore();
     const percentage = (score / quizData.length) * 100;
     return (
       <div className="h-full overflow-y-auto p-4 md:p-6 bg-slate-50 animate-fade-in">
         <div className="max-w-2xl mx-auto">
           <div className="w-full bg-white p-8 rounded-2xl shadow-lg text-center mb-6">
               <h3 className="text-3xl font-bold text-gray-800 mb-1">Quiz Complete!</h3>
               <p className="text-gray-600 mb-6">
                 {percentage === 100 ? "Outstanding! A perfect score!" : percentage >= 70 ? "Great job! You know your stuff." : "Good effort! Keep practicing."}
               </p>
              
              <div className="grid grid-cols-2 gap-4 text-left my-8">
                <div className="bg-gray-100 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Score</p>
                  <p className="text-2xl font-bold text-blue-600">{score}/{quizData.length}</p>
                </div>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Time</p>
                  <p className="text-2xl font-bold text-blue-600">{formatTime(timer)}</p>
                </div>
              </div>
           </div>

           <div className="w-full bg-white p-6 rounded-2xl shadow-lg">
             <h4 className="text-lg font-bold text-gray-800 mb-4">Review Answers</h4>
             <div className="space-y-4">
               {quizData.map((q, i) => {
                 const userAnswer = selectedAnswers[q.id];
                 const correctIndex = resolveCorrectIndex(q); // CHANGED
                 const isCorrect = userAnswer !== 'skipped' && userAnswer !== undefined && Number(userAnswer) === Number(correctIndex);
                 const userAnswerLabel = userAnswer === 'skipped' ? 'Skipped' : (userAnswer !== undefined ? q.options[userAnswer] : 'Not answered');
                 const correctAnswerLabel = q.options[correctIndex];
                 return (
                   <div key={q.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                     <p className="font-semibold text-gray-700 mb-2">{i + 1}. {q.question}</p>
                     <div className={`flex items-center p-3 rounded-lg text-sm ${isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                       {isCorrect ? <CheckCircle size={16} className="mr-2 flex-shrink-0" /> : <XCircle size={16} className="mr-2 flex-shrink-0" />}
                       Your answer: <span className="font-semibold ml-1">{userAnswerLabel}</span>
                     </div>
                     {!isCorrect && userAnswer !== undefined && userAnswer !== 'skipped' && (
                       <div className="flex items-center p-3 rounded-lg text-sm mt-2 bg-green-50 text-green-800">
                         <CheckCircle size={16} className="mr-2 flex-shrink-0" />
                         Correct answer: <span className="font-semibold ml-1">{correctAnswerLabel}</span>
                       </div>
                     )}
                     {userAnswer === 'skipped' && (
                       <div className="flex items-center p-3 rounded-lg text-sm mt-2 bg-green-50 text-green-800">
                         <CheckCircle size={16} className="mr-2 flex-shrink-0" />
                         Correct answer: <span className="font-semibold ml-1">{correctAnswerLabel}</span>
                       </div>
                     )}
                   </div>
                 )
               })}
             </div>
           </div>
         </div>
       </div>
     );
   }

   return (
     <div className="p-4 md:p-6 max-w-3xl mx-auto h-full flex flex-col bg-white mb-10">
      {/* Navigation bar at top */}
      <div className="flex items-center mb-4">
        <button onClick={handleTryAgain} className="flex items-center text-gray-600 hover:text-black font-medium">
          <ArrowLeft size={16} className="mr-2" /> Back
        </button>
        
        <div className="ml-auto flex items-center gap-3">
          {/** New Quiz button commented out per request */}
          {/**
          <button 
            onClick={() => generateQuiz(true)}
            className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
          >
            <RefreshCw size={16} className="mr-2" /> New Quiz
          </button>
          */}
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-4 mb-6">
        <span className="text-sm font-medium text-gray-500">{currentQuestion + 1}</span>
        <div className="w-full bg-gray-200 rounded-full h-1">
          <div className="bg-black h-1 rounded-full transition-all duration-300" style={{ width: `${((currentQuestion + 1) / quizData.length) * 100}%` }}></div>
        </div>
        <span className="text-sm font-medium text-gray-500">{quizData.length}</span>
      </div>

      {/* Question content */}
      <div className={`flex-grow ${questionAnimation}`}>
        <div className="flex justify-between items-start mb-8">
          <div className="pr-4 w-full">
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
              {currentQ.question}
            </ReactMarkdown>
          </div>
          <button onClick={handleCopy} className="text-gray-500 hover:text-black flex-shrink-0 mt-1 relative">
            <Copy size={20} />
            {isCopied && <span className="absolute -top-6 -left-2 text-xs bg-black text-white px-2 py-0.5 rounded">Copied!</span>}
          </button>
        </div>
        <div className="space-y-4">
          {currentQ.options.map((option, index) => {
            const letter = String.fromCharCode(65 + index);
            const correctIndex = resolveCorrectIndex(currentQ); // CHANGED
            let optionClass = 'border-gray-200 hover:border-black';
            let feedbackIcon = null;
            let animationClass = '';

            if (answerStatus !== 'unanswered') {
              if (index === correctIndex) {
                optionClass = 'border-green-500 bg-green-50';
                feedbackIcon = <CheckCircle size={20} className="text-green-500" />;
                if (index === selectedOption) animationClass = 'animate-pulse-correct';
              } else if (index === selectedOption) {
                optionClass = 'border-red-500 bg-red-50';
                feedbackIcon = <XCircle size={20} className="text-red-500" />;
                animationClass = 'animate-shake';
              }
            } else if (index === selectedOption) {
              optionClass = 'border-black bg-gray-50';
            }
            
            return (
              <div key={index} onClick={() => handleOptionSelect(index)} className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${optionClass} ${animationClass}`}>
                <div className="flex items-center">
                  <span className="text-gray-500 font-semibold mr-4">{letter}.</span>
                  <span className="font-medium text-gray-800">
                    {/* Render option markdown */}
                    <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                      {option}
                    </ReactMarkdown>
                  </span>
                </div>
                {feedbackIcon}
              </div>
            );
          })}
        </div>
      </div>

      {/* Answer explanation */}
      {answerStatus !== 'unanswered' && currentQ && currentQ.explanation && (
        <div className="mt-6 p-4 rounded-lg bg-gray-100 border border-gray-200 animate-slide-up">
          <p className="font-semibold text-gray-800 mb-1">Explanation</p>
          <div className="text-sm text-gray-700">
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
              {currentQ.explanation}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {/* Bottom navigation controls */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          {/* Previous question button - only show when not on first question and answered */}
          <button 
            onClick={handlePrevQuestion} 
            disabled={currentQuestion === 0}
            className={`p-3 rounded-md ${currentQuestion > 0 ? 'hover:bg-gray-100 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="flex items-center gap-4">
            {answerStatus === 'unanswered' ? (
              <>
                <button onClick={handleDontKnow} className="px-8 py-3 rounded-lg font-semibold text-gray-700 border border-gray-300 hover:bg-gray-100">Don't know</button>
                <button onClick={handleCheckAnswer} disabled={selectedOption === null} className="p-3 rounded-lg font-semibold text-white bg-black disabled:bg-gray-400 flex items-center"><Check size={24} /></button>
              </>
            ) : (
              <button onClick={handleNextQuestion} className={`px-8 py-3 rounded-lg font-semibold text-white flex items-center transition-colors ${answerStatus === 'correct' ? 'bg-green-500 hover:bg-green-600' : answerStatus === 'incorrect' ? 'bg-red-600 hover:bg-red-700' : 'bg-black'}`}>
                {currentQuestion < quizData.length - 1 ? 'Next Question' : 'Finish Quiz'}
                <ArrowRight size={18} className="ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
